import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, Checkbox, CodeField, DefinitionList, Dialog, EmptyState, Inline, Link, Menu, PageHeader, Panel, Select, Skeleton, Spinner, Stack, t, Text, toast, Tooltip,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { RefusalPage } from '../components/Refusal';
import { PendingButton } from '../components/PendingButton';
import { EnvironmentVersions } from '../components/EnvironmentVersions';
import { EnvironmentTasks } from '../components/EnvironmentTasks';
import { EnvironmentRetire } from '../components/EnvironmentRetire';
import { EnvironmentVariables } from '../components/EnvironmentVariables';
import { DeploymentStatus } from '../components/DeploymentStatus';
import { api, ApiFailure, unwrap } from '../lib/api';
import { relativeTime, shortVersion } from '../lib/format';
import { ACCESS_USER, AccessFields, drawAccessPassword } from '../components/install/AccessFields';

type Environment = maintenance.operations['getEnvironment']['responses']['200']['content']['application/json'];
type Observed = NonNullable<Environment['observed']>;
type Operation = maintenance.components['schemas']['InstallationOperation'];
type Tab = 'overview' | 'versions' | 'variables' | 'logs' | 'tasks';

const OBSERVED_TONE: Record<Observed['state'], 'pos' | 'warn' | 'neg' | 'neutral'> = { concordant: 'pos', gap: 'warn', degraded: 'neg', maintenance: 'neutral', unknown: 'neutral' };
const SINCE = ['15m', '1h', '6h', '24h'] as const;
const LINES = ['200', '500', '2000', '5000'] as const;

/**
 * SPEC-034 § 9 and § 12 (lot 1.5): the page of an environment the platform
 * installed — the version declared and the one observed on the server,
 * compared by digest, then its operation: the logs on demand (secrets masked
 * on the server) and the restart. DSM page type « Record ».
 */
export function EnvironmentPage() {
  const { deployment = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (['logs', 'versions', 'variables', 'tasks'] as const).find(x => x === params.get('tab')) ?? 'overview';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const env = useQuery({
    queryKey: ['environment', deployment],
    queryFn: async () => unwrap(await api.maintenance.GET('/environments/{deployment}', { params: { path: { deployment } } })) as Environment,
    // Every 3 seconds while a retirement runs (its steps are followed on the page), else every 30.
    refetchInterval: q => (q.state.data && ['retiring', 'unlinking', 'purging', 'resetting'].includes(q.state.data.state) ? 3_000 : 30_000),
    retry: false,
  });
  const e = env.data;

  // The logs: one reading at a time, followed until the engine has it.
  const [service, setService] = useState<string>('');
  const [since, setSince] = useState<(typeof SINCE)[number]>('1h');
  const [lines, setLines] = useState<(typeof LINES)[number]>('500');
  const [operation, setOperation] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const op = useQuery({
    queryKey: ['environment-operation', e?.id, operation],
    queryFn: async () => unwrap(await api.maintenance.GET('/installations/{id}/operations/{operation}', { params: { path: { id: e!.id, operation: operation! } } })) as Operation,
    enabled: Boolean(e && operation),
    refetchInterval: q => (q.state.data?.status === 'running' || !q.state.data ? 2_000 : false),
  });

  const [restarting, setRestarting] = useState(false);
  // § 12.5: the maintenance actions, each confirmed.
  type Upkeep = 'maintenance_on' | 'maintenance_off' | 'stop' | 'start';
  const [upkeep, setUpkeep] = useState<Upkeep | null>(null);
  const [restartService, setRestartService] = useState<string>('');
  const [stepUp, setStepUp] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [importing, setImporting] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreFrom, setRestoreFrom] = useState('');
  // D100: « Changer le mot de passe »: a new one drawn here (shown to copy before saving), or the protection removed.
  const [accessEdit, setAccessEdit] = useState<{ protect: boolean; user: string; password: string } | null>(null);
  const [accessSending, setAccessSending] = useState(false);

  // D94: an environment belongs to its project (Projets › project › Environnements).
  const crumbs = [
    { label: t('nav.projects'), href: '/projects' },
    ...(e ? [{ label: e.project, href: `/projects/${encodeURIComponent(e.project)}?tab=environments` }] : []),
    { label: deployment },
  ];
  if (env.error) return <Shell crumbs={crumbs}><RefusalPage error={env.error} /></Shell>;

  const saveAccess = async () => {
    if (!e || !accessEdit) return;
    setAccessSending(true);
    try {
      unwrap(await api.maintenance.PUT('/installations/{id}/access', {
        params: { path: { id: e.id } }, body: { access: accessEdit.protect ? { user: accessEdit.user, password: accessEdit.password } : null },
      }));
      setAccessEdit(null);
      toast({ tone: 'info', title: t('access.saved') });
      void queryClient.invalidateQueries({ queryKey: ['environment', deployment] });
    } catch (err) {
      if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { setAccessEdit(null); setStepUp(true); return; }
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setAccessSending(false);
    }
  };

  const start = async (action: 'logs' | 'restart' | Upkeep) => {
    if (!e) return;
    setStarting(true);
    try {
      if (action === 'maintenance_on' || action === 'maintenance_off' || action === 'stop' || action === 'start') {
        const path = { params: { path: { id: e.id } } };
        const r = unwrap(action === 'stop' ? await api.maintenance.POST('/installations/{id}/stop', path)
          : action === 'start' ? await api.maintenance.POST('/installations/{id}/start', path)
          : await api.maintenance.POST('/installations/{id}/maintenance', { ...path, body: { on: action === 'maintenance_on' } }));
        setUpkeep(null);
        setOperation(r.operation);
        toast({ tone: 'info', title: t(`environment.upkeep.${action}.started`) });
        void queryClient.invalidateQueries({ queryKey: ['environment', deployment] });
      } else if (action === 'logs') {
        const r = unwrap(await api.maintenance.POST('/installations/{id}/logs', { params: { path: { id: e.id } }, body: { ...(service ? { service } : {}), since, lines: Number(lines) } }));
        setOperation(r.operation);
      } else {
        const r = unwrap(await api.maintenance.POST('/installations/{id}/restart', { params: { path: { id: e.id } }, body: restartService ? { service: restartService } : {} }));
        setRestarting(false);
        setOperation(r.operation);
        toast({ tone: 'info', title: t('environment.restart.started', { target: restartService || t('environment.restart.all') }) });
        void queryClient.invalidateQueries({ queryKey: ['environment', deployment] });
      }
    } catch (err) {
      if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { setRestarting(false); setUpkeep(null); setStepUp(true); return; }
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setStarting(false);
    }
  };

  const observed = e?.observed ?? null;
  const services = e?.services ?? [];
  const serviceOptions = [{ value: '', label: t('environment.logs.allServices') }, ...services.map(s => ({ value: s, label: s }))];
  const go = (id: Tab) => setParams(id === 'overview' ? {} : { tab: id }, { replace: true });
  const reading = op.data?.action === 'logs' ? op.data : null;
  // § 11.3: the outcome of a restoration test (the other operations say theirs by the environment's state).
  const restored = op.data?.action === 'restore' && op.data.status !== 'running' ? op.data : null;
  const restore = async () => {
    if (!e) return;
    setStarting(true);
    try {
      const r = unwrap(await api.maintenance.POST('/installations/{id}/restore', { params: { path: { id: e.id } }, body: { from: restoreFrom } }));
      setRestoring(false);
      setOperation(r.operation);
      toast({ tone: 'info', title: t('environment.restore.started', { from: restoreFrom }) });
    } catch (err) {
      if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { setRestoring(false); setStepUp(true); return; }
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setStarting(false);
    }
  };

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.deployment')}
      title={deployment}
      // D96: one status — what runs (observed), else, while nothing was read on the server, the installation's state.
      status={e ? <DeploymentStatus state={e.state} observed={observed?.state} /> : undefined}
      subtitle={e ? [e.server, e.domain ? `https://${e.domain}` : null, e.release ? t('environment.declared', { version: shortVersion(e.release) }) : null].filter(Boolean).join(' · ') : undefined}
      actions={e && (e.allowed.restart || e.allowed.upkeep || e.allowed.retire || e.restoreFrom.length > 0) && ['deployed', 'in_service'].includes(e.state)
        ? (
          <Menu
            label={t('environment.upkeep.menu')}
            align="end"
            trigger={<Button variant="secondary" size="sm" icon="build">{t('environment.upkeep.menu')}</Button>}
            items={[
              ...(!e.stopped && e.allowed.restart ? [{ label: t('environment.restart.action'), onSelect: () => setRestarting(true) }] : []),
              ...(!e.stopped && e.allowed.upkeep ? [e.maintenance
                ? { label: t('environment.upkeep.maintenance_off.action'), onSelect: () => setUpkeep('maintenance_off') }
                : { label: t('environment.upkeep.maintenance_on.action'), onSelect: () => setUpkeep('maintenance_on') }] : []),
              ...(e.allowed.upkeep ? [e.stopped
                ? { label: t('environment.upkeep.start.action'), onSelect: () => setUpkeep('start') }
                : { label: t('environment.upkeep.stop.action'), onSelect: () => setUpkeep('stop') }] : []),
              ...(e.restoreFrom.length ? [{ label: t('environment.restore.action'), onSelect: () => { setRestoreFrom(e.restoreFrom[0]!); setRestoring(true); } }] : []),
              ...(e.allowed.retire ? [{ separator: true as const }, { label: t('environment.retire.menu'), onSelect: () => setRetiring(true), danger: true }] : []),
            ]}
          />
        )
        : undefined}
      tabs={{
        label: t('environment.tabs.label'),
        value: tab,
        onChange: id => go(id as Tab),
        items: [{ id: 'overview', label: t('environment.tabs.overview') }, { id: 'versions', label: t('environment.tabs.versions') }, ...(e?.canOperate ? [{ id: 'variables', label: t('environment.tabs.variables') }] : []), ...(e?.allowed.logs ? [{ id: 'logs', label: t('environment.tabs.logs') }] : []), ...(e?.allowed.task ? [{ id: 'tasks', label: t('environment.tabs.tasks') }] : [])],
        action: tab === 'versions' && e?.allowed.deploy && ['deployed', 'in_service'].includes(e.state)
          ? <Button size="sm" icon="rocket_launch" onClick={() => setDeploying(true)}>{t('environment.versions.deploy')}</Button>
          : tab === 'variables' && e?.allowed.secret
          ? <Button size="sm" icon="upload" onClick={() => setImporting(true)}>{t('environment.variables.import.action')}</Button>
          : tab === 'logs' && e?.allowed.logs
          ? <PendingButton size="sm" icon="article" pending={starting || reading?.status === 'running'} disabled={!['deployed', 'in_service'].includes(e.state)} onClick={() => void start('logs')}>{t('environment.logs.read')}</PendingButton>
          : undefined,
      }}
    />
  );

  return (
    <Shell crumbs={crumbs} header={header}>
      {!e ? <Stack gap={24}><Skeleton lines={3} /><Skeleton lines={6} /></Stack> : (
        <Stack gap={24}>
          {restored && (
            <Alert tone={restored.status === 'succeeded' ? 'pos' : 'neg'} title={t(restored.status === 'succeeded' ? 'environment.restore.succeeded' : 'environment.restore.failed')}>
              {restored.status === 'succeeded' ? t('environment.restore.succeededBody') : restored.failure}
            </Alert>
          )}
          <EnvironmentRetire env={e} canOperate={e.allowed.retire} asking={retiring} onAskClose={() => setRetiring(false)} onStepUp={() => setStepUp(true)} />
          {e.stopped && <Alert tone="warn" title={t('environment.upkeep.stoppedTitle')}>{t('environment.upkeep.stoppedBody')}</Alert>}
          {e.maintenance && !e.stopped && <Alert tone="info" title={t('environment.upkeep.maintenanceTitle')}>{t('environment.upkeep.maintenanceBody', { domain: e.domain })}</Alert>}
          {observed?.unknownBecause === 'agent' && (
            <Alert tone="info" title={t('environment.observed.agentTitle')} action={t('environment.observed.agentAction')} onAction={() => navigate(`/servers/${encodeURIComponent(e.server)}?tab=configuration`)}>
              {t('environment.observed.agentBody', { server: e.server })}
            </Alert>
          )}
          {/* § 9: say which container differs, or that none of the deployment is seen on the server. */}
          {observed?.state === 'gap' && (
            <Alert tone="warn" title={t('environment.observed.gapTitle')}>
              {observed.outOfLine.some(o => o.reason === 'digest')
                ? t('environment.observed.gapDigest', { containers: observed.outOfLine.filter(o => o.reason === 'digest').map(o => o.name).join(', ') })
                : t('environment.observed.gapNone')}
            </Alert>
          )}
          {observed?.state === 'degraded' && <Alert tone="neg" title={t('environment.observed.degradedTitle')}>{observed.outOfLine.map(o => `${o.name} (${t(`environment.observed.reason.${o.reason}`)})`).join(' · ')}</Alert>}

          {tab === 'overview' && (
            <Panel srTitle={t('environment.overview.title')}>
              <DefinitionList items={[
                { key: 'server', label: t('environment.overview.server'), value: <Link href={`/servers/${encodeURIComponent(e.server)}`}>{e.server}</Link> },
                { key: 'address', label: t('environment.overview.address'), value: e.domain ? <Link href={`https://${e.domain}`} external>{`https://${e.domain}`}</Link> : <Text variant="body-sm" tone="mute">{t('environment.overview.internal')}</Text> },
                // D109: the entry points the repository declares (kanbios.links), on the project's domain in this environment.
                ...(e.entryPoints?.length ? [{ key: 'entryPoints', label: t('environment.overview.entryPoints'), value: (
                  <Stack gap={4}>{e.entryPoints.map(p => <Link key={p.url} href={p.url} external>{`${p.label} · ${p.url}`}</Link>)}</Stack>
                ) }] : []),
                { key: 'repository', label: t('environment.overview.repository'), value: <Text variant="code">{`${e.repository} @ ${e.ref}`}</Text> },
                {
                  key: 'declared', label: t('environment.overview.declared'),
                  value: e.release ? <Tooltip content={e.release}><Text variant="code">{shortVersion(e.release)}</Text></Tooltip> : <Text variant="body-sm" tone="mute">{t('environment.overview.none')}</Text>,
                },
                {
                  key: 'observed', label: t('environment.overview.observed'),
                  value: observed ? (
                    <Stack gap={4}>
                      <Inline gap={8}>
                        <Badge tone={OBSERVED_TONE[observed.state]} size="sm">{t(`environment.observed.${observed.state}`)}</Badge>
                        {observed.versions.length > 0 && <Text variant="code">{observed.versions.map(shortVersion).join(', ')}</Text>}
                      </Inline>
                      <Text variant="caption" tone="mute">{observed.readAt ? t('environment.observed.readAt', { when: relativeTime(observed.readAt) }) : t('environment.observed.neverRead')}</Text>
                    </Stack>
                  ) : <Text variant="body-sm" tone="mute">{t('environment.overview.none')}</Text>,
                },
                // D100: out of production, the access behind a password (never in production: the site is public).
                ...(e.environmentType !== 'production' ? [{
                  key: 'access', label: t('environment.overview.access'),
                  value: (
                    <Stack gap={4}>
                      <Inline gap={8} align="center">
                        <Text variant="body-sm">{e.access.protected ? t('access.protected', { user: e.access.user ?? '' }) : t('access.public')}</Text>
                        {e.allowed.secret && e.state === 'in_service' && !e.access.pending && (
                          <Button variant="secondary" size="sm" icon="key" onClick={() => setAccessEdit({ protect: true, user: e.access.user ?? 'kanbios', password: drawAccessPassword() })}>{t('access.change')}</Button>
                        )}
                      </Inline>
                      {e.access.pending && <Text variant="caption" tone="mute">{t('access.pending')}</Text>}
                      {e.access.failed && <Text variant="caption" tone="neg">{t('access.failed', { failure: e.failure ?? '' })}</Text>}
                    </Stack>
                  ),
                }] : []),
                { key: 'installation', label: t('environment.overview.installation'), // Its follow-up while it is installed; once retiring, its steps are on this page.
                  value: ['retire_requested', 'retiring', 'unlinking', 'retired', 'purging', 'resetting', 'purged'].includes(e.state)
                    ? t(`environment.state.${e.state}`)
                    : <Link href={`/servers/${encodeURIComponent(e.server)}/install?installation=${encodeURIComponent(e.id)}`}>{t(`environment.state.${e.state}`)}</Link> },
              ]} />
            </Panel>
          )}

          {tab === 'versions' && <EnvironmentVersions env={e} deploying={deploying} onDeployClose={() => setDeploying(false)} onStepUp={() => setStepUp(true)} />}

          {tab === 'variables' && e.canOperate && <EnvironmentVariables installation={e.id} production={e.environmentType === 'production'} canWrite={e.allowed.secret} canRedeploy={e.allowed.deploy} onStepUp={() => setStepUp(true)} importing={importing} onImportClose={() => setImporting(false)} />}

          {tab === 'tasks' && e.allowed.task && <EnvironmentTasks installation={e.id} production={e.environmentType === 'production'} onStepUp={() => setStepUp(true)} />}

          {tab === 'logs' && e.allowed.logs && (
            <Stack gap={16}>
              <Text variant="body-sm" tone="mute">{t('environment.logs.lead')}</Text>
              <Inline gap={12}>
                <Select label={t('environment.logs.service')} size="sm" options={serviceOptions} value={service} onChange={v => setService(v ?? '')} />
                <Select label={t('environment.logs.since')} size="sm" options={SINCE.map(s => ({ value: s, label: t(`environment.logs.sinceOptions.${s}`) }))} value={since} onChange={v => v && setSince(v as (typeof SINCE)[number])} />
                <Select label={t('environment.logs.lines')} size="sm" options={LINES.map(l => ({ value: l, label: l }))} value={lines} onChange={v => v && setLines(v as (typeof LINES)[number])} />
              </Inline>
              {!reading && <EmptyState framed={false} icon="article" title={t('environment.logs.emptyTitle')}>{t('environment.logs.emptyBody')}</EmptyState>}
              {reading?.status === 'failed' && <Alert tone="neg" title={t('environment.logs.failed')}>{reading.failure}</Alert>}
              {reading?.status === 'running' && <Inline gap={8} align="center"><Spinner size={16} label={t('environment.logs.running')} /><Text variant="body-sm" tone="mute">{t('environment.logs.running')}</Text></Inline>}
              {reading?.status === 'succeeded' && (
                <Stack gap={8}>
                  {reading.truncated && <Alert tone="info" title={t('environment.logs.truncated')}>{t('environment.logs.truncatedBody')}</Alert>}
                  <CodeField label={t('environment.logs.output', { n: reading.lines.length })} value={reading.lines.join('\n') || t('environment.logs.empty')} readOnly lineNumbers maxHeight={560} />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      )}

      <Dialog
        open={upkeep !== null}
        onClose={() => setUpkeep(null)}
        // Stopping cuts the site off: a destructive action (DSM Dialog tone danger), as the restoration and the retirement.
        tone={upkeep === 'stop' ? 'danger' : 'default'}
        title={upkeep ? t(`environment.upkeep.${upkeep}.title`, { deployment }) : ''}
        description={upkeep ? t(`environment.upkeep.${upkeep}.body`, { domain: e?.domain ?? '' }) : ''}
        confirmLabel={upkeep ? t(`environment.upkeep.${upkeep}.action`) : ''}
        confirmDisabled={starting}
        onConfirm={() => upkeep && void start(upkeep)}
      />
      <Dialog
        open={restarting}
        onClose={() => setRestarting(false)}
        title={t('environment.restart.title', { deployment })}
        description={t('environment.restart.body')}
        confirmLabel={t('environment.restart.confirm')}
        confirmDisabled={starting}
        onConfirm={() => void start('restart')}
      >
        <Select label={t('environment.logs.service')} options={[{ value: '', label: t('environment.restart.all') }, ...services.map(s => ({ value: s, label: s }))]} value={restartService} onChange={v => setRestartService(v ?? '')} />
      </Dialog>
      <Dialog
        open={restoring}
        onClose={() => setRestoring(false)}
        tone="danger"
        title={t('environment.restore.title', { deployment })}
        description={t('environment.restore.body')}
        confirmLabel={t('environment.restore.confirm')}
        confirmDisabled={starting || !restoreFrom}
        onConfirm={() => void restore()}
      >
        <Select label={t('environment.restore.from')} options={(e?.restoreFrom ?? []).map(d => ({ value: d, label: d }))} value={restoreFrom} onChange={v => setRestoreFrom(v ?? '')} />
      </Dialog>
      <Dialog
        open={accessEdit !== null}
        onClose={() => setAccessEdit(null)}
        title={t('access.dialogTitle', { deployment })}
        description={t('access.dialogBody', { domain: e?.domain ?? '' })}
        confirmLabel={t('access.save')}
        confirmDisabled={accessSending || Boolean(accessEdit?.protect && !ACCESS_USER.test(accessEdit.user))}
        onConfirm={() => void saveAccess()}
      >
        {accessEdit && (
          <Stack gap={12}>
            <Checkbox label={t('access.protect')} checked={accessEdit.protect} onChange={protect => setAccessEdit({ ...accessEdit, protect })} />
            {accessEdit.protect
              ? <AccessFields idPrefix="access-edit" user={accessEdit.user} password={accessEdit.password}
                  onUser={user => setAccessEdit({ ...accessEdit, user })} onPassword={password => setAccessEdit({ ...accessEdit, password })}
                  error={ACCESS_USER.test(accessEdit.user) ? undefined : t('access.errors.user')} />
              : <Alert tone="warn" title={t('access.publicWarnTitle')}>{t('access.publicWarnBody')}</Alert>}
          </Stack>
        )}
      </Dialog>
      <Dialog
        open={stepUp}
        onClose={() => setStepUp(false)}
        title={t('settings.enrolment.stepUp.title')}
        description={t('environment.stepUp')}
        confirmLabel={t('settings.enrolment.stepUp.confirm')}
        onConfirm={() => window.location.assign(`/api/auth/session/step-up?returnTo=${encodeURIComponent(`/environments/${deployment}`)}`)}
      />
    </Shell>
  );
}
